import { useRef, useState } from "react";
import { toast } from "sonner";
import {
    Mic,
    StopCircle,
    Play,
    RotateCcw,
    Sparkles,
    Loader2,
    AlertCircle,
    CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/api";

const AXES = [
    { key: "sons", label: "Sons" },
    { key: "accent_tonique", label: "Accent tonique" },
    { key: "rythme", label: "Rythme" },
    { key: "intonation", label: "Intonation" },
    { key: "fluidite", label: "Fluidité" },
];

export default function PronunciationPractice({
    sentences,
    selectedSentence,
    setSelectedSentence,
    language,
}) {
    const [status, setStatus] = useState("idle"); // idle | recording | recorded | analyzing
    const [recordingBlob, setRecordingBlob] = useState(null);
    const [recordingUrl, setRecordingUrl] = useState(null);
    const [analysis, setAnalysis] = useState(null);

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeCandidates = [
                "audio/webm;codecs=opus",
                "audio/webm",
                "audio/ogg;codecs=opus",
                "audio/mp4",
            ];
            const mimeType =
                mimeCandidates.find((m) =>
                    typeof MediaRecorder !== "undefined" &&
                    MediaRecorder.isTypeSupported &&
                    MediaRecorder.isTypeSupported(m),
                ) || "";
            const mr = mimeType
                ? new MediaRecorder(stream, { mimeType })
                : new MediaRecorder(stream);
            chunksRef.current = [];
            mr.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            mr.onstop = () => {
                const type = mr.mimeType || "audio/webm";
                const blob = new Blob(chunksRef.current, { type });
                setRecordingBlob(blob);
                setRecordingUrl(URL.createObjectURL(blob));
                stream.getTracks().forEach((t) => t.stop());
                setStatus("recorded");
            };
            mediaRecorderRef.current = mr;
            mr.start();
            setStatus("recording");
            setAnalysis(null);
        } catch (err) {
            toast.error(
                "Impossible d'accéder au microphone. Autorisez l'accès dans le navigateur.",
            );
            setStatus("idle");
        }
    };

    const stopRecording = () => {
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== "inactive") mr.stop();
    };

    const resetRecording = () => {
        if (recordingUrl) URL.revokeObjectURL(recordingUrl);
        setRecordingBlob(null);
        setRecordingUrl(null);
        setAnalysis(null);
        setStatus("idle");
    };

    const analyze = async () => {
        if (!recordingBlob || !selectedSentence) return;
        setStatus("analyzing");
        setAnalysis(null);
        try {
            const form = new FormData();
            const ext = (recordingBlob.type || "audio/webm").includes("mp4") ? "mp4" : "webm";
            form.append("audio", recordingBlob, `recording.${ext}`);
            form.append("expected_sentence", selectedSentence);
            form.append("language", language);
            const { data } = await api.post("/poc/analyze-pronunciation", form, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            setAnalysis(data);
            if (data.status === "error") {
                toast.warning("Audio non analysable — voir détails ci-dessous.");
            } else {
                toast.success(`Score global : ${data.overall_score}/100`);
            }
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || "Erreur d'analyse.";
            toast.error(`Analyse : ${detail}`);
        } finally {
            setStatus("recorded");
        }
    };

    return (
        <section
            className="bg-white border border-lexiBorder rounded-2xl p-6 md:p-8"
            data-testid="pronunciation-section"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-xs tracking-[0.24em] uppercase text-inkSoft mb-2">
                        Prononciation
                    </p>
                    <h3 className="font-serif text-2xl leading-tight">
                        Répétez, l&apos;IA vous écoute
                    </h3>
                </div>
                <div className="text-[11px] text-inkSoft border border-lexiBorder rounded-full px-3 py-1">
                    Whisper + GPT-4.1 · analyse 5 axes
                </div>
            </div>

            {sentences.length === 0 && (
                <p className="mt-6 text-sm text-inkSoft" data-testid="pron-no-sentence">
                    Générez d&apos;abord une leçon pour choisir une phrase à prononcer.
                </p>
            )}

            {sentences.length > 0 && (
                <>
                    <div className="mt-6 space-y-2" data-testid="sentence-picker">
                        <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft font-semibold">
                            Choisissez une phrase à prononcer
                        </p>
                        <div className="grid grid-cols-1 gap-2">
                            {sentences.map((s, i) => {
                                const active = s === selectedSentence;
                                return (
                                    <button
                                        key={i}
                                        onClick={() => setSelectedSentence(s)}
                                        data-testid={`sentence-option-${i}`}
                                        className={`text-left px-4 py-3 rounded-xl border transition-colors font-serif italic text-base leading-snug ${
                                            active
                                                ? "bg-ink text-white border-ink"
                                                : "bg-paper border-lexiBorder hover:bg-paperAlt"
                                        }`}
                                    >
                                        « {s} »
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Recorder */}
                    <div
                        className="mt-6 rounded-2xl border border-lexiBorder bg-paper p-5"
                        data-testid="recorder-block"
                    >
                        <div className="flex flex-wrap items-center gap-3">
                            {status !== "recording" && !recordingUrl && (
                                <button
                                    onClick={startRecording}
                                    data-testid="record-start-btn"
                                    className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-5 py-2.5 text-sm font-medium active:scale-[0.98] transition-transform"
                                >
                                    <Mic className="w-4 h-4" />
                                    S&apos;enregistrer
                                </button>
                            )}
                            {status === "recording" && (
                                <button
                                    onClick={stopRecording}
                                    data-testid="record-stop-btn"
                                    className="inline-flex items-center gap-2 bg-ink text-white rounded-full px-5 py-2.5 text-sm font-medium active:scale-[0.98] transition-transform"
                                >
                                    <StopCircle className="w-4 h-4 animate-pulse" />
                                    Arrêter
                                </button>
                            )}
                            {recordingUrl && (
                                <>
                                    <audio
                                        src={recordingUrl}
                                        controls
                                        data-testid="recording-playback"
                                        className="h-10 rounded"
                                    />
                                    <button
                                        onClick={resetRecording}
                                        data-testid="record-reset-btn"
                                        className="inline-flex items-center gap-1.5 border border-lexiBorder bg-white text-ink hover:bg-paperAlt rounded-full px-4 py-2 text-sm transition-colors"
                                    >
                                        <RotateCcw className="w-3.5 h-3.5" />
                                        Recommencer
                                    </button>
                                    <button
                                        onClick={analyze}
                                        disabled={status === "analyzing"}
                                        data-testid="analyze-btn"
                                        className="inline-flex items-center gap-2 bg-emerald hover:bg-emeraldSoft text-white rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60 active:scale-[0.98] transition-transform"
                                    >
                                        {status === "analyzing" ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Sparkles className="w-4 h-4" />
                                        )}
                                        {status === "analyzing"
                                            ? "Analyse…"
                                            : "Analyser la prononciation"}
                                    </button>
                                </>
                            )}
                        </div>
                        {status === "recording" && (
                            <p
                                className="text-xs text-inkSoft mt-3 flex items-center gap-2"
                                data-testid="recording-status"
                            >
                                <span className="w-2 h-2 rounded-full bg-terracotta animate-pulse" />
                                Enregistrement en cours…
                            </p>
                        )}
                    </div>

                    {/* Analysis */}
                    {analysis && <AnalysisResult analysis={analysis} />}
                </>
            )}
        </section>
    );
}

function AnalysisResult({ analysis }) {
    const isError = analysis.status === "error";

    return (
        <div
            className={`mt-6 rounded-2xl border p-6 animate-fade-up ${
                isError
                    ? "border-terracotta/30 bg-terracotta/8"
                    : "border-lexiBorder bg-white"
            }`}
            data-testid="analysis-result"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    {isError ? (
                        <AlertCircle className="w-5 h-5 text-terracotta" />
                    ) : (
                        <CheckCircle2 className="w-5 h-5 text-emerald" />
                    )}
                    <h4 className="font-serif text-xl">
                        {isError ? "Audio non analysable" : "Analyse de prononciation"}
                    </h4>
                </div>
                {!isError && (
                    <div
                        className="text-right"
                        data-testid="analysis-overall-score-block"
                    >
                        <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft">
                            Score global
                        </p>
                        <p
                            className="font-serif text-3xl leading-none mt-1"
                            data-testid="analysis-overall-score"
                        >
                            {analysis.overall_score}
                            <span className="text-inkSoft text-lg">/100</span>
                        </p>
                    </div>
                )}
            </div>

            {isError && (
                <p
                    className="mt-3 text-sm text-ink"
                    data-testid="analysis-error-reason"
                >
                    {analysis.error_reason}
                </p>
            )}

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft mb-1">
                        Attendu
                    </p>
                    <p
                        className="font-serif italic text-base leading-snug"
                        data-testid="analysis-expected"
                    >
                        « {analysis.expected_sentence} »
                    </p>
                </div>
                <div>
                    <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft mb-1">
                        Entendu (Whisper)
                    </p>
                    <p
                        className="font-mono text-sm text-inkSoft break-words"
                        data-testid="analysis-heard"
                    >
                        {analysis.heard_transcription || "—"}
                    </p>
                </div>
            </div>

            {!isError && analysis.axes && (
                <div className="mt-6 space-y-4" data-testid="analysis-axes">
                    {AXES.map((axis) => {
                        const data = analysis.axes[axis.key] || {};
                        const score = Number(data.score) || 0;
                        return (
                            <div key={axis.key} data-testid={`axis-${axis.key}`}>
                                <div className="flex justify-between items-baseline">
                                    <span className="text-sm font-medium">{axis.label}</span>
                                    <span
                                        className="text-xs font-mono text-inkSoft"
                                        data-testid={`axis-${axis.key}-score`}
                                    >
                                        {score}/100
                                    </span>
                                </div>
                                <div className="mt-1 h-2 rounded-full bg-paperAlt overflow-hidden">
                                    <div
                                        className="h-full bg-emerald transition-all duration-500 ease-out"
                                        style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
                                    />
                                </div>
                                {data.comment && (
                                    <p className="mt-1 text-xs text-inkSoft leading-snug">
                                        {data.comment}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {analysis.targeted_tip && (
                <div
                    className={`mt-6 rounded-xl p-4 ${
                        isError
                            ? "bg-white border border-lexiBorder"
                            : "bg-emerald text-white"
                    }`}
                    data-testid="analysis-tip"
                >
                    <p
                        className={`text-[10px] tracking-[0.24em] uppercase mb-1 ${
                            isError ? "text-inkSoft" : "text-white/70"
                        }`}
                    >
                        Conseil ciblé
                    </p>
                    <p className={`text-sm leading-relaxed ${isError ? "text-ink" : ""}`}>
                        {analysis.targeted_tip}
                    </p>
                </div>
            )}
        </div>
    );
}
